// 专业推荐工作流调用工具
const COZE_API_URL = 'https://api.coze.cn/v1/workflow/run'
const COZE_TOKEN_URL = '/ai/coze/get-online'
const MAJOR_RECOMMENDATION_WORKFLOW_ID = '7570990702311358502'

/**
 * 获取Coze Token
 * @returns {Promise<string>} Token字符串
 */
async function getCozeToken() {
  try {
    const authHeader = localStorage.getItem('authorization')
    
    const response = await fetch(COZE_TOKEN_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader
      }
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`获取Token失败: ${response.status} ${response.statusText}`)
    }
    
    const data = await response.json()
    
    if (data && data.success && data.data && data.data.access_token) {
      return data.data.access_token
    } else {
      throw new Error('获取token失败: ' + JSON.stringify(data))
    }
  } catch (error) {
    console.error('获取Token错误:', error)
    throw error
  }
}

/**
 * 将用户画像数据转换为AI输入格式
 * @param {Object} formData - 用户填写的表单数据
 * @returns {string} 格式化的输入字符串
 */
function formatUserProfileForAI(formData) {
  const careerTypeMap = {
    'realistic': '现实型',
    'investigative': '研究型',
    'artistic': '艺术型',
    'social': '社会型',
    'enterprising': '企业型',
    'conventional': '常规型'
  }
  
  const familyResourceMap = {
    'tech': '科技/IT',
    'finance': '金融/经济',
    'education': '教育/科研',
    'medical': '医疗/健康',
    'government': '政府/公共服务',
    'business': '商业/贸易',
    'manufacturing': '制造业',
    'media': '媒体/文化'
  }
  
  const economicSupportMap = {
    'high': '高',
    'medium': '中',
    'low': '低'
  }
  
  const riskPreferenceMap = {
    'high': '高风险高回报',
    'medium': '中等风险',
    'low': '低风险稳定'
  }
  
  // 构建输入文本
  const inputLines = []
  
  if (formData.scoreRange) {
    inputLines.push(`高考分数段：${formData.scoreRange}`)
  }
  
  if (formData.subjects && formData.subjects.length > 0) {
    inputLines.push(`优势学科：${formData.subjects.join('、')}`)
  }
  
  if (formData.careerType) {
    inputLines.push(`职业兴趣特质：${careerTypeMap[formData.careerType] || formData.careerType}`)
  }
  
  if (formData.excludeFields && formData.excludeFields.length > 0) {
    inputLines.push(`排斥领域：${formData.excludeFields.join('、')}`)
  }
  
  if (formData.familyResource) {
    inputLines.push(`家庭行业资源：${familyResourceMap[formData.familyResource] || formData.familyResource}`)
  }
  
  if (formData.economicSupport) {
    inputLines.push(`经济支撑能力：${economicSupportMap[formData.economicSupport] || formData.economicSupport}`)
  }
  
  if (formData.targetRegions && formData.targetRegions.length > 0) {
    inputLines.push(`目的就业地：${formData.targetRegions.join('、')}`)
  }
  
  if (formData.riskPreference) {
    inputLines.push(`风险偏好：${riskPreferenceMap[formData.riskPreference] || formData.riskPreference}`)
  }
  
  return inputLines.join('\n')
}

/**
 * 解析AI返回的专业推荐结果（JSON格式）
 * @param {Object} workflowData - 工作流返回的原始数据
 * @returns {Object} 解析后的推荐结果
 */
function parseRecommendationResult(workflowData) {
  try {
    // 处理可能的字符串格式数据
    let data = workflowData
    if (typeof workflowData === 'string') {
      data = JSON.parse(workflowData)
    }
    
    // 如果data字段本身是字符串，需要再次解析
    if (typeof data.data === 'string') {
      try {
        const parsedData = JSON.parse(data.data)
        data = { ...data, ...parsedData }
      } catch (e) {
        console.error('解析data字段失败:', e.message)
      }
    }
    
    // 获取output字段，支持多种可能的字段名
    const output = data.output || data.output3 || ''
    
    // 如果output为空，输出错误信息用于调试
    if (!output) {
      console.error('未找到AI输出内容，检查data结构:', Object.keys(data))
    }
    
    let majors = []
    
    // 尝试解析JSON格式的推荐结果
    try {
      let jsonContent = output
      
      // 如果包含markdown代码块，提取其中的JSON内容
      if (output.includes('```json')) {
        const jsonMatch = output.match(/```json\s*\n([\s\S]*?)\n```/)
        if (jsonMatch) {
          jsonContent = jsonMatch[1]
        }
      }
      
      const jsonResult = JSON.parse(jsonContent)
      
      if (jsonResult.top3 && Array.isArray(jsonResult.top3)) {
        majors = jsonResult.top3.map(item => ({
          rank: item.rank,
          name: item.major,
          score: item.score.toString(),
          details: formatMajorDetails(item),
          rawDetails: JSON.stringify(item, null, 2),
          // 保存原始JSON数据用于详细显示
          scoreMatch: item.score_match,
          interestMatch: item.interest_match,
          familyMatch: item.family_match,
          highlights: item.highlights,
          concerns: item.concerns,
          vsTop1: item.vs_top1,
          whyRank3: item.why_rank3
        }))
        
        
        return {
          majors: majors.slice(0, 3),
          rawOutput: output,
          success: true,
          logic: jsonResult.logic,
          excluded: jsonResult.excluded
        }
      } else {
        console.error('JSON格式正确但未找到top3数组:', jsonResult)
        return {
          majors: [],
          rawOutput: output,
          success: false,
          error: 'JSON格式正确但未找到top3专业数组'
        }
      }
    } catch (jsonError) {
      console.error('JSON解析失败:', jsonError.message)
      console.error('原始输出内容:', output)
      
      return {
        majors: [],
        rawOutput: output,
        success: false,
        error: `JSON解析失败: ${jsonError.message}`
      }
    }
    
  } catch (error) {
    console.error('解析推荐结果失败:', error)
    return {
      majors: [],
      rawOutput: workflowData,
      success: false,
      error: error.message
    }
  }
}

/**
 * 格式化专业详情信息
 * @param {Object} majorItem - 专业信息对象
 * @returns {string} 格式化后的详情文本
 */
function formatMajorDetails(majorItem) {
  const details = []
  
  if (majorItem.score_match) {
    details.push(`**分数适配**: ${majorItem.score_match}`)
  }
  
  if (majorItem.interest_match) {
    details.push(`**兴趣匹配**: ${majorItem.interest_match}`)
  }
  
  if (majorItem.family_match) {
    details.push(`**家庭资源**: ${majorItem.family_match}`)
  }
  
  if (majorItem.highlights) {
    details.push(`**专业亮点**: ${majorItem.highlights}`)
  }
  
  if (majorItem.concerns) {
    details.push(`**注意事项**: ${majorItem.concerns}`)
  }
  
  if (majorItem.vs_top1) {
    details.push(`**与TOP1对比**: ${majorItem.vs_top1}`)
  }
  
  if (majorItem.why_rank3) {
    details.push(`**排名原因**: ${majorItem.why_rank3}`)
  }
  
  return details.join('\n\n')
}

// 全局取消标志
let cancelRecommendation = false

/**
 * 取消当前推荐请求
 */
export function cancelCurrentRecommendation() {
  cancelRecommendation = true
}

/**
 * 调用专业推荐工作流（异步处理）
 * @param {Object} formData - 用户填写的表单数据
 * @param {Function} onProgress - 进度回调函数
 * @param {number} retryCount - 重试次数
 * @returns {Promise<Object>} 推荐结果
 */
export async function getMajorRecommendations(formData, onProgress = null, retryCount = 0) {
  // 重置取消标志
  cancelRecommendation = false
  try {
    // 格式化用户输入
    const inputText = formatUserProfileForAI(formData)
    
    if (!inputText) {
      throw new Error('请先完整填写用户画像信息')
    }
    
    // 获取Token
    const token = await getCozeToken()
    
    // 构建请求体
    const requestData = {
      workflow_id: MAJOR_RECOMMENDATION_WORKFLOW_ID,
      parameters: {
        input: inputText
      }
    }
    
    
    // 通知开始处理
    if (onProgress) {
      onProgress('分析中，请稍候...', 10)
    }
    
    // 调用工作流API
    const response = await fetch(COZE_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestData)
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`工作流API请求失败: ${response.status} ${response.statusText}, 详情: ${errorText}`)
    }
    
    const data = await response.json()
    
    if (data.code === 0 && data.data) {
      // 检查是否有execute_id，表示需要异步轮询
      if (data.data.execute_id && !data.data.output) {
        // 异步处理，需要轮询结果
        if (onProgress) {
          onProgress('分析中，请稍候...', 30)
        }
        return await pollWorkflowResult(data.data.execute_id, token, onProgress)
      } else {
        // 同步处理，直接解析结果
        const parsedResult = parseRecommendationResult(data.data)
        
        if (onProgress) {
          onProgress('分析完成！', 100)
        }
        
        return parsedResult
      }
    } else {
      const errorMsg = data.msg || data.message || '工作流执行失败'
      const errorCode = data.code || '未知错误码'
      throw new Error(`调用工作流失败 (code: ${errorCode}): ${errorMsg}`)
    }
    
  } catch (error) {
    // 系统异常重试机制
    const maxRetries = 2
    if (error.message.includes('5000') && retryCount < maxRetries) {
      console.log(`系统异常，5秒后进行第${retryCount + 1}次重试...`)
      await new Promise(resolve => setTimeout(resolve, 5000))
      return await getMajorRecommendations(formData, retryCount + 1)
    }
    
    console.error('专业推荐失败:', error)
    throw error
  }
}

/**
 * 轮询工作流执行结果
 * @param {string} executeId - 执行ID
 * @param {string} token - 认证token
 * @param {Function} onProgress - 进度回调
 * @returns {Promise<Object>} 推荐结果
 */
async function pollWorkflowResult(executeId, token, onProgress = null) {
  const POLL_URL = 'https://api.coze.cn/v1/workflow/retrieve'
  const MAX_POLL_TIME = 180000 // 最大轮询时间3分钟
  const POLL_INTERVAL = 3000 // 轮询间隔3秒
  
  const startTime = Date.now()
  let pollCount = 0
  
  while (Date.now() - startTime < MAX_POLL_TIME) {
    // 检查是否被取消
    if (cancelRecommendation) {
      throw new Error('用户取消了AI推荐')
    }
    
    pollCount++
    
    try {
      if (onProgress) {
        const progress = Math.min(30 + (pollCount * 10), 90)
        onProgress(`分析中... (${Math.floor((Date.now() - startTime) / 1000)}s)`, progress)
      }
      
      const response = await fetch(`${POLL_URL}?execute_id=${executeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        throw new Error(`轮询请求失败: ${response.status}`)
      }
      
      const data = await response.json()
      console.log(`轮询第${pollCount}次结果:`, data)
      
      if (data.code === 0 && data.data) {
        // 检查执行状态
        if (data.data.status === 'SUCCESS' && data.data.output) {
          // 执行成功，解析结果
          const parsedResult = parseRecommendationResult(data.data)
          
          if (onProgress) {
            onProgress('分析完成！', 100)
          }
          
          return parsedResult
        } else if (data.data.status === 'FAILED') {
          // 执行失败
          throw new Error(`工作流执行失败: ${data.data.error_message || '未知错误'}`)
        } else if (data.data.status === 'RUNNING') {
          // 仍在执行中，继续轮询
          console.log('工作流仍在执行中，继续轮询...')
        }
      }
      
      // 等待下次轮询
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      
    } catch (error) {
      console.error(`轮询第${pollCount}次失败:`, error)
      
      // 如果是网络错误，继续重试
      if (pollCount < 3) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        continue
      } else {
        throw error
      }
    }
  }
  
  // 超时
  throw new Error('AI推荐超时，请稍后重试')
}

/**
 * 创建加载提示弹窗（支持进度显示）
 */
export function createRecommendationLoadingModal() {
  const loadingHtml = `
    <div id="recommendation-loading-modal" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10001;
    ">
      <div style="
        background: white;
        border-radius: 12px;
        padding: 30px;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        min-width: 300px;
        max-width: 400px;
        width: 90%;
      ">
        <div style="
          width: 50px;
          height: 50px;
          border: 4px solid #e3f2fd;
          border-top: 4px solid #3b82f6;
          border-radius: 50%;
          margin: 0 auto 20px;
          animation: spin 1s linear infinite;
        "></div>
        <h3 id="loading-title" style="
          margin: 0 0 20px 0;
          color: #3b82f6;
          font-size: 18px;
          font-weight: bold;
        ">🎓 AI 正在分析您的画像...</h3>
        <p id="loading-status" style="
          margin: 0 0 20px 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
          text-align: center;
        ">分析中，请稍候...</p>
        <button id="cancel-recommendation" style="
          margin-top: 15px;
          padding: 8px 16px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          border-radius: 4px;
          color: #666;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        " onmouseover="this.style.background='#e0e0e0'" onmouseout="this.style.background='#f5f5f5'">
          取消推荐
        </button>
        <div style="
          margin-top: 20px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #3b82f6;
          font-size: 12px;
          color: #666;
        ">
          💡 提示：AI 将为您推荐 TOP3 最适合的专业
        </div>
      </div>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  `
  
  const modalContainer = document.createElement('div')
  modalContainer.innerHTML = loadingHtml
  document.body.appendChild(modalContainer)
  
  // 添加取消按钮事件监听
  const cancelButton = document.getElementById('cancel-recommendation')
  if (cancelButton) {
    cancelButton.addEventListener('click', () => {
      cancelCurrentRecommendation()
      removeRecommendationLoadingModal()
    })
  }
}

/**
 * 更新加载状态
 * @param {string} status - 状态文本
 * @param {number} progress - 进度百分比 (已废弃，保持兼容性)
 */
export function updateLoadingProgress(status, progress) {
  const statusElement = document.getElementById('loading-status')
  
  if (statusElement) {
    statusElement.textContent = status
  }
}

/**
 * 移除加载提示弹窗
 */
export function removeRecommendationLoadingModal() {
  const modal = document.getElementById('recommendation-loading-modal')
  if (modal && modal.parentElement) {
    modal.parentElement.remove()
  }
}

